<template>
  <div class="flight-check-in-status">
    <div class="flight-check-in-status__row">
      <div
        :class="[
          'flight-check-in-status__status',
          `flight-check-in-status__status--${status_info.status}`,
        ]"
      >
        <vb-icon
          :name="status_info.icon"
          :size="16"
          :color="sass(status_info.color)"
        />
        <span>
          {{ status_info.display }}
        </span>
        <span
          v-if="!is_homogenous_state && itinerary_label(flight.itinerary_key)"
        >
          ({{ itinerary_label(flight.itinerary_key) }})
        </span>
      </div>
    </div>
    <div class="flight-check-in-status__row" v-html="status_info.description()">
    </div>
    <div
      v-for="(boarding_passes, itinerary_key) in boarding_passes_per_itinerary"
      :key="`${itinerary_key}`"
      class="flight-check-in-status__row"
    >
      <div class="flight-check-in-status__boarding-passes">
        <a
          v-for="(boarding, boarding_index) in boarding_passes"
          :key="`${itinerary_key}:${boarding.passenger_journey_id}:${boarding_index}`"
          :href="boarding.public_url"
          class="flight-check-in-status__link"
        >
          <div> {{ boarding.traveler_label }} </div>
          <vb-icon name="chevron_right" :size="8" color="--info" />
        </a>
      </div>
    </div>
  </div>
</template>

<script>
  import { get_config, is_set } from '@/utilities';
  import { traveler_name } from '@liveflight/utilities';

  const PENDING_STATUS = {
    icon: 'hourglass',
    status: 'pending',
    color: 'primary',
    display: 'Richiesta in attesa',
    description: () =>
      "Qualche giorno prima del volo potresti ricevere un'email per l'invio dei dati personali necessari per il check-in. Una volta completato, riceverai le carte d'imbarco via email e lo stato del servizio verrà aggiornato su questa pagina.",
  };

  const POSSIBLE_STATUS_INFO = Object.fromEntries(
    [
      PENDING_STATUS,
      {
        icon: 'cross_circle',
        status: 'not_requested',
        color: 'base',
        display: 'Servizio non richiesto',
        description: () =>
          'Non hai richiesto il servizio di check-in automatico per questa prenotazione.',
      },
      {
        icon: 'check_circle',
        status: 'success',
        color: 'success',
        display: 'Richiesta emessa',
        description: () =>
          'Le tue carte d’imbarco sono state inviate al tuo indirizzo email. Puoi anche scaricarle in formato PDF cliccando sul link sottostante per averle sempre a portata di mano.',
      },
      ...['failed', 'canceled'].map((status) => ({
        icon: 'cross_circle',
        color: 'danger',
        status,
        display: 'Richiesta fallita',
        description: () =>
          `Si è verificato un problema tecnico durante l'emissione delle tue carte d'imbarco. Ti invitiamo a contattare il nostro servizio di assistenza ${get_config('site.emailSupport') ? `all’indirizzo email <a href="mailto:${get_config('site.emailSupport')}">${get_config('site.emailSupport')}</a>` : ''} il prima possibile per ricevere supporto in merito.`,
      })),
    ].map((element) => [element.status, element])
  );

  export default {
    name: 'flight-check-in-status',
    props: {
      flight: { type: Object, required: true },
      is_homogenous_state: { type: Boolean, default: false },
    },
    computed: {
      boarding_passes_per_itinerary() {
        const boarding_passes = {
          [this.flight.itinerary_key]: this.flight.boarding_passes || [],
        };

        return Object.fromEntries(
          Object.entries(boarding_passes)
            .map((entry) => [
              entry[0],
              this.ensure_passenger_label(entry[1] || []),
            ])
            .filter((entry) => (entry[1] || []).length)
        );
      },
      status_info() {
        const key = String(this.flight.status).toLowerCase().trim();
        return POSSIBLE_STATUS_INFO[key] || PENDING_STATUS;
      },
      has_multiple_boarding_pass_itineraries() {
        return Object.keys(this.all_boarding_passes).length > 1;
      },
    },
    methods: {
      itinerary_label(itinerary_key) {
        if (!itinerary_key) {
          return '';
        }
        const key_parts = String(itinerary_key).split('-');
        return `${key_parts[0]} - ${key_parts[1]}`;
      },
      passenger_index(passengers) {
        const index = {};
        (passengers || []).forEach((passenger) => {
          const { id, passenger_journey_id } = passenger || {};
          if (is_set(id)) {
            index[`id:${id}`] = passenger;
          }
          if (is_set(passenger_journey_id)) {
            index[`journey:${passenger_journey_id}`] = passenger;
          }
        });
        return index;
      },
      resolve_passenger(boarding_pass, passengers) {
        const { passenger_id, passenger_journey_id } = boarding_pass || {};
        let passenger = {};
        if (is_set(passenger_id) && passengers[`id:${passenger_id}`]) {
          passenger = passengers[`id:${passenger_id}`];
        } else if (
          is_set(passenger_journey_id) &&
          passengers[`journey:${passenger_journey_id}`]
        ) {
          passenger = passengers[`journey:${passenger_journey_id}`];
        }
        return passenger;
      },
      ensure_passenger_label(boarding_passes) {
        const passengers = this.passenger_index(this.flight.passengers);
        return boarding_passes.map((boarding_pass, index) => {
          const { name, last_name } = this.resolve_passenger(
            boarding_pass,
            passengers
          );
          let traveler_label = traveler_name({
            firstname: 'Passeggero',
            lastname: String(index + 1),
          });
          if (name && last_name) {
            traveler_label = traveler_name({
              firstname: name,
              lastname: last_name,
            });
          }
          return {
            ...boarding_pass,
            traveler_label,
          };
        });
      },
    },
  };
</script>

<style lang="scss" scoped>
  .flight-check-in-status {
    line-height: normal;
    display: flex;
    flex-direction: column;
    gap: $grid-gutter-width / 3;
    font-size: 14px;

    &__status {
      display: flex;
      gap: 7.5px;
      align-items: center;

      font-size: 16px;
      font-weight: $vb-font-weight-semibold;

      &--success {
        color: $success;
      }
      &--canceled,
      &--failed {
        color: $danger;
      }
      &--pending {
        color: var(--primary);
      }
    }

    &__boarding-passes {
      margin-top: $grid-gutter-width / 6;
      display: flex;
      flex-wrap: wrap;
      row-gap: $grid-gutter-width / 2;
      column-gap: $grid-gutter-width * 2 / 3;
    }

    &__itinerary {
      margin-bottom: 5px;
      font-size: 16px;
      font-weight: $vb-font-weight-semibold;
    }

    &__link {
      display: flex;
      align-items: center;
      justify-content: center;
      text-decoration: underline;
      gap: 3px;
    }
  }
</style>
